import { useEffect } from "react";

import type { IUseTourScrollLockProps } from "@/providers/types";

// NOTE: NSAI-5895 — the page must not be scrollable while a guided tour runs.
//
// The app's own layout is `h-full`, so the document is normally exactly the
// height of the viewport and there is nothing to scroll — which is why QA saw
// scrolling "correctly blocked" on most steps. That was never a guarantee,
// though, only a side effect. Joyride positions its overlay and spotlight
// absolutely at the target's DOCUMENT coordinates, and on a step whose target
// sits below the fold of a scrolled sidebar those coordinates land past the
// viewport: the document grows (measured at 691px against a 600px viewport on
// the chat-folders step), the page becomes scrollable, and it then drifts under
// a spotlight Joyride had already measured. Every earlier step measured exactly
// 600px and could not scroll at all — the difference QA reported as "step 10
// behaves differently from the other steps".
//
// Pinning the root element for the duration of the tour makes the blocked
// scrolling deliberate on every step instead of incidental on most of them. It
// mirrors what `MainSidebar` already does to its own scrollport while
// `isRunning`, and the previous inline value is restored on unlock so a page
// that sets its own root overflow is left as it was found.
const ROOT_OVERFLOW_LOCK = "hidden";

// NOTE: Reference-counted, because `TourShell` is not a singleton: `App.tsx`
// nests `CallAssistantTourProvider` inside `MainTourProvider`, so two instances
// of this hook are mounted at once on every authenticated page. Saving and
// restoring per instance would break the moment their locks overlap — the
// second to lock would capture `"hidden"` as its "previous" value and restore
// that on unlock, leaving the page permanently unscrollable with no way back
// but a reload. Only the first lock reads the real value and only the last
// unlock writes it back.
let lockCount = 0;
let overflowBeforeFirstLock = "";

export const useTourScrollLock = ({
  isLocked,
}: IUseTourScrollLockProps): void => {
  // NOTE: The unlocked branch returns a no-op teardown rather than `undefined`
  // so the effect has one return type — `consistent-return` rejects the mixed
  // shape, and an early bare `return` here reads as "nothing to clean up",
  // which is exactly what the no-op says out loud.
  useEffect((): (() => void) => {
    if (!isLocked) return () => undefined;

    const rootStyle = document.documentElement.style;

    if (lockCount === 0) {
      overflowBeforeFirstLock = rootStyle.overflow;
      rootStyle.overflow = ROOT_OVERFLOW_LOCK;
    }
    lockCount += 1;

    return (): void => {
      lockCount -= 1;
      if (lockCount === 0) {
        rootStyle.overflow = overflowBeforeFirstLock;
        overflowBeforeFirstLock = "";
      }
    };
  }, [isLocked]);
};
